// apps/web/src/pages/qualidade/nc-list-page.tsx — Fase 02, Plano 09 (QUAL-01..03, D-13, D-14).
import { useState, useEffect, type ReactElement } from 'react';
import { DestinacaoDialog } from '../../components/qualidade/destinacao-dialog';
import { MaterialNcBadge } from '../../components/shared/material-nc-badge';
import { rotuloDestinacao } from '../../lib/rotulos';
import { TablePagination } from '../../components/ui/table-pagination';

export interface NaoConformidadeRow {
  readonly id: string;
  readonly apontamentoId: string;
  readonly osId: string;
  readonly loteId: string | null;
  readonly motivo: string;
  readonly quantidade: number;
  readonly status: 'ABERTA' | 'DESTINADA';
  readonly destinacao: string | null;
  readonly osCorretivaId: string | null;
  readonly actorUserId: string | null;
  readonly destinadaAt: string | null;
  readonly createdAt: string;
  readonly celulaOrigemId: string | null;
  readonly celulaOrigemCodigo: string | null;
  readonly loteCodigo: string | null;
  readonly opNumero: string | null;
}

export function NcListPage(): ReactElement {
  const [ncs, setNcs] = useState<readonly NaoConformidadeRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  // Dialog de Destinação
  const [selectedNc, setSelectedNc] = useState<NaoConformidadeRow | null>(null);

  const fetchNcs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/qualidade/nc?page=${page}&limit=${pageSize}`);
      if (!res.ok) {
        throw new Error('Falha ao carregar lista de Não Conformidades.');
      }
      const response = (await res.json()) as {
        data: NaoConformidadeRow[];
        meta: { total: number };
      };
      setNcs(response.data);
      setTotal(response.meta.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao buscar NCs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchNcs();
  }, [page, pageSize]);

  const ncsAbertas = ncs.filter((nc) => nc.status === 'ABERTA');

  const getStatusBadge = (nc: NaoConformidadeRow) => {
    if (nc.status === 'ABERTA') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
          Aberta (Aguardando Decisão)
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700">
        Destinada ({rotuloDestinacao(nc.destinacao)})
      </span>
    );
  };

  return (
    <div className="nc-list-page min-h-screen bg-slate-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <header className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Não conformidades</h1>
            <p className="text-sm text-slate-500 mt-1">
              {ncsAbertas.length === 1
                ? '1 não conformidade em aberto aguardando destinação'
                : `${ncsAbertas.length} não conformidades em aberto aguardando destinação`}
            </p>
          </div>
          <div>
            <MaterialNcBadge temNc={ncsAbertas.length > 0} />
          </div>
        </header>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl font-medium" role="alert">
            {error}
          </div>
        )}

        {/* Tabela de Não Conformidades */}
        <main className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-500">Carregando Não Conformidades...</div>
          ) : ncs.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">Nenhuma não conformidade registrada. As ocorrências identificadas nos apontamentos aparecerão aqui.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="p-4">Motivo</th>
                    <th className="p-4">Origem</th>
                    <th className="p-4">Quantidade</th>
                    <th className="p-4">Estado</th>
                    <th className="p-4 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ncs.map((nc) => (
                    <tr key={nc.id} className="hover:bg-slate-50">
                      <td className="p-4 text-xs text-slate-900 max-w-xs">
                        {nc.motivo}
                      </td>
                      <td className="p-4 font-mono text-xs text-slate-600 break-all">
                        {nc.loteCodigo && nc.opNumero ? `Lote ${nc.loteCodigo} · OP ${nc.opNumero}` : `OS técnica ${nc.osId}`}
                      </td>
                      <td className="p-4 font-mono font-bold text-slate-800">
                        {nc.quantidade} un
                      </td>
                      <td className="p-4">{getStatusBadge(nc)}</td>
                      <td className="p-4 text-right">
                        {nc.status === 'ABERTA' ? (
                          <button
                            type="button"
                            onClick={() => setSelectedNc(nc)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-sm transition"
                          >
                            Destinar
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400 font-mono">
                            {nc.osCorretivaId ? `OS corretiva #${nc.osCorretivaId.slice(0, 6)}` : 'Sem reentrada'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={total}
                itemLabel="não conformidades"
                onPageChange={setPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize);
                  setPage(1);
                }}
              />
            </div>
          )}
        </main>
      </div>

      {/* Dialog de Destinação */}
      {selectedNc && (
        <DestinacaoDialog
          ncId={selectedNc.id}
          motivo={selectedNc.motivo}
          quantidade={selectedNc.quantidade}
          celulaOrigemId={selectedNc.celulaOrigemId}
          celulaOrigemCodigo={selectedNc.celulaOrigemCodigo}
          open={Boolean(selectedNc)}
          onClose={() => setSelectedNc(null)}
          onDestinado={() => void fetchNcs()}
        />
      )}
    </div>
  );
}
